import { proethicaModuleListMeta } from "@constants/demo/proethica-modules";
import {
	type ProethicaTestDescriptor,
	type ProethicaTestKind,
	getProethicaTestByCollectionId,
} from "@constants/demo/proethica-tests";
import {
	getModuleMetaKey,
	getProethicaEntryShortLabel,
} from "@features/dashboard/helpers/proethica-module-list";
import type { MixedItemTag } from "@memorang/types";

export type ProethicaNextEntryChip =
	| { kind: "duration"; label: string }
	| { kind: "activities"; count: number }
	| { kind: "knowledgeCheck"; count: number }
	| { kind: "questions"; count: number };

export type ProethicaNextEntryMeta = {
	title: string;
	description: string;
	chips: ProethicaNextEntryChip[];
	thumbnailSource?: string | number | { uri: string };
	testKind?: ProethicaTestKind;
};

export type ProethicaReportNextStep =
	| { kind: "entry"; entry: MixedItemTag }
	| { kind: "resolving" }
	| { kind: "home" };

// An empty roster means the learning path hasn't landed yet, not that it is
// finished - only a resolved path with no entries left promotes Go home.
export const resolveProethicaReportNextStep = (
	nextEntry: MixedItemTag | undefined,
	isResolvingPath: boolean,
): ProethicaReportNextStep => {
	if (nextEntry) {
		return { kind: "entry", entry: nextEntry };
	}
	return isResolvingPath ? { kind: "resolving" } : { kind: "home" };
};

const testEntryMeta = (
	test: ProethicaTestDescriptor,
	tag: MixedItemTag,
): ProethicaNextEntryMeta => {
	const questionCount = tag.numItems ?? 0;
	return {
		title: test.title,
		description: test.subtitle,
		chips: questionCount ? [{ kind: "questions", count: questionCount }] : [],
		testKind: test.kind,
	};
};

// The card previews authored curriculum content, so everything but the question
// count comes from the static module/test catalog rather than the live roster.
// An entry the catalog doesn't know still yields a card - the Continue CTA it
// carries is the report's primary action and must never go missing.
export const resolveProethicaNextEntryMeta = (
	tag: MixedItemTag,
): ProethicaNextEntryMeta => {
	const test = getProethicaTestByCollectionId(tag.id);
	if (test) {
		return testEntryMeta(test, tag);
	}

	const meta = proethicaModuleListMeta[getModuleMetaKey(tag)];
	if (!meta) {
		return {
			title: getProethicaEntryShortLabel(tag),
			description: "",
			chips: [],
		};
	}

	const chips: ProethicaNextEntryChip[] = [
		{ kind: "duration", label: meta.durationLabel },
	];
	if (meta.activityCount) {
		chips.push({ kind: "activities", count: meta.activityCount });
	}
	if (meta.knowledgeCheckQuestionCount) {
		chips.push({
			kind: "knowledgeCheck",
			count: meta.knowledgeCheckQuestionCount,
		});
	}

	return {
		title: meta.titleWithNumber,
		description: meta.subtitle,
		chips,
		thumbnailSource: meta.thumbnailSource,
	};
};
